/**
 * Premium Select over @kobalte/core (same pattern as dialog/tooltip/popover):
 * portaled, animated panel, keyboard/typeahead/ARIA built in, theme-safe.
 *
 * Meant for simple string lists (preserves value/onChange/options of the
 * native `<select>` it replaces), but supports arbitrary types via
 * `optionTextValue`.
 */
import { Select as KobalteSelect } from "@kobalte/core/select";
import type { CollectionNode } from "@kobalte/core";
import { Check, ChevronsUpDown, Plus } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@journal/shared";

/**
 * Listbox item. Lives at module level (not redefined on every render) and uses
 * the `textValue` that Kobalte computes from `optionTextValue`.
 */
function SelectItem<T>(itemProps: { item: CollectionNode<T> }): JSX.Element {
  return (
    <KobalteSelect.Item
      item={itemProps.item}
      class={cn(
        "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-sm text-popover-foreground outline-none",
        "transition-colors data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      )}
    >
      <KobalteSelect.ItemLabel>{itemProps.item.textValue}</KobalteSelect.ItemLabel>
      <KobalteSelect.ItemIndicator>
        <Check class="h-4 w-4" />
      </KobalteSelect.ItemIndicator>
    </KobalteSelect.Item>
  );
}

/**
 * CTA rendered inside the dropdown when the Select has no options
 * (AWS style: an empty combobox offers "Create X" / "Go to X" instead of a
 * dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or
 * `href` to render a link (navigate to the screen that provides the
 * options). Provide exactly one. `can` (default `true`) gates the CTA
 * by permission — when it is `false`, the empty dropdown falls back to a "no
 * options" message and the creation action is not shown. Omit `emptyAction`
 * entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Default true. */
  can?: boolean;
  /** CTA leading icon. Default: a "plus" glyph. */
  icon?: JSX.Element;
  /** Optional helper line above the CTA (e.g. "No lists yet"). */
  description?: string;
}

interface SelectProps<T> {
  value: T;
  onChange: (value: T) => void;
  options: T[];
  /** Visible label of each option (by default, the value itself stringified). */
  optionTextValue?: (option: T) => string;
  placeholder?: string;
  disabled?: boolean;
  /** Trigger class (the visible button). */
  class?: string;
  /** Width of the dropdown panel (by default, the same as the trigger). */
  contentClass?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to
   * the place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Empty dropdown message when there is no CTA (allowed). */
  emptyLabel?: string;
}

export function Select<T>(props: SelectProps<T>): JSX.Element {
  const [local, others] = splitProps(props, [
    "value",
    "onChange",
    "options",
    "optionTextValue",
    "placeholder",
    "disabled",
    "class",
    "contentClass",
    "aria-label",
    "data-testid",
    "emptyAction",
    "emptyLabel",
  ]);

  const textValue = (option: T) =>
    local.optionTextValue ? local.optionTextValue(option) : String(option);

  const isEmpty = () => local.options.length === 0;

  return (
    <KobalteSelect<T>
      value={local.value}
      // Single-select required: re-clicking the ALREADY selected option must NOT
      // toggle it to empty. By default Kobalte emits `null` when re-clicking the
      // current selection (deselect) → here it was ignored but the listbox's
      // internal state was left de-selected (empty field before submit). With
      // `disallowEmptySelection` the value can CHANGE to another option but not be
      // emptied through interaction. (CC: Select re-click de-selects.)
      disallowEmptySelection
      onChange={(value) => {
        // With `disallowEmptySelection` Kobalte no longer emits null on a re-click;
        // we keep the guard as a defensive floor for the non-nullable contract.
        if (value !== null) {
          local.onChange(value);
        }
      }}
      options={local.options}
      optionTextValue={textValue}
      disabled={local.disabled}
      placeholder={local.placeholder}
      itemComponent={SelectItem}
      {...others}
    >
      <KobalteSelect.Trigger
        aria-label={local["aria-label"]}
        data-testid={local["data-testid"]}
        class={cn(
          "inline-flex h-8 items-center justify-between gap-2 rounded-lg border border-border/50 bg-muted/30 px-2.5 text-sm text-foreground outline-none transition-colors",
          "hover:border-primary/30 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring/40",
          "disabled:cursor-not-allowed disabled:opacity-50",
          local.class,
        )}
      >
        <KobalteSelect.Value<T> class="truncate">
          {(state) => {
            const o = state.selectedOption();
            return o == null ? "" : textValue(o);
          }}
        </KobalteSelect.Value>
        <KobalteSelect.Icon class="shrink-0 opacity-60">
          <ChevronsUpDown class="h-3.5 w-3.5" />
        </KobalteSelect.Icon>
      </KobalteSelect.Trigger>
      <KobalteSelect.Portal>
        <KobalteSelect.Content
          class={cn(
            "ui-select-content z-[220] overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-xl outline-none",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <KobalteSelect.Listbox class="max-h-60 overflow-y-auto overscroll-contain" />
          </Show>
        </KobalteSelect.Content>
      </KobalteSelect.Portal>
    </KobalteSelect>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the CTA from
 * `emptyAction` (creation button or navigation link) when present
 * and allowed; otherwise, a dimmed "no options" message.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor) and inherits the
 * panel's opacity-only entrance (`ui-select-content`), so it respects
 * `prefers-reduced-motion` through the global guard like the rest of the UI. Do
 * not add transform (it would break the no-flicker guard).
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays
              i18n-clean; el em-dash neutro es el placeholder de último recurso
              cuando no se da un label. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
